import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScreenHeading } from './screen-heading'

// Same rule as the company form on the organisation selector.
const MINIMUM_NAME_LENGTH = 3

export type CompanyDetails = {
	name: string
	website: string | null
}

type Props = {
	/** Set once the company exists. Only its name can change then. */
	currentName: string | null
	onSubmit: (company: CompanyDetails) => void
	isSubmitting: boolean
}

// People type "acme.com", so the protocol is optional. A host without a dot can't be a public website.
function toWebsiteUrl(input: string): string | null {
	const url = /^https?:\/\//i.test(input) ? input : `https://${input}`
	try {
		return new URL(url).hostname.includes('.') ? url : null
	} catch {
		return null
	}
}

export function CompanyScreen({ currentName, onSubmit, isSubmitting }: Props) {
	const { t } = useTranslation()
	const [name, setName] = useState(currentName ?? '')
	const [website, setWebsite] = useState('')
	const [showNameError, setShowNameError] = useState(false)
	const [showWebsiteError, setShowWebsiteError] = useState(false)

	const trimmedName = name.trim()
	const trimmedWebsite = website.trim()

	const handleSubmit = (event: FormEvent) => {
		event.preventDefault()
		const websiteUrl = trimmedWebsite === '' ? null : toWebsiteUrl(trimmedWebsite)
		const isNameValid = trimmedName.length >= MINIMUM_NAME_LENGTH
		const isWebsiteValid = trimmedWebsite === '' || websiteUrl !== null

		setShowNameError(!isNameValid)
		setShowWebsiteError(!isWebsiteValid)
		if (!isNameValid || !isWebsiteValid) { return }

		onSubmit({ name: trimmedName, website: websiteUrl })
	}

	return (
		<>
			<ScreenHeading title={t('onboarding.company.title')} subtitle={t('onboarding.company.subtitle')} />
			<form onSubmit={handleSubmit} className="flex flex-col gap-2" noValidate>
				<label htmlFor="onboarding-company-name" className="text-sm font-medium">
					{t('onboarding.company.nameLabel')}
				</label>
				<Input
					id="onboarding-company-name"
					autoComplete="organization"
					autoFocus
					value={name}
					onChange={event => {
						setName(event.target.value)
						setShowNameError(false)
					}}
					aria-invalid={showNameError}
					aria-describedby={showNameError ? 'onboarding-company-error' : undefined}
					disabled={isSubmitting}
					className="h-14 text-base"
				/>
				{showNameError && (
					<p id="onboarding-company-error" className="text-sm text-destructive">
						{t('onboarding.company.tooShort')}
					</p>
				)}
				{currentName === null && (
					<>
						<label htmlFor="onboarding-company-website" className="mt-3 text-sm font-medium">
							{t('onboarding.company.websiteLabel')}
						</label>
						<Input
							id="onboarding-company-website"
							autoComplete="url"
							inputMode="url"
							placeholder="acme.com"
							value={website}
							onChange={event => {
								setWebsite(event.target.value)
								setShowWebsiteError(false)
							}}
							aria-invalid={showWebsiteError}
							aria-describedby={showWebsiteError
								? 'onboarding-company-website-error'
								: 'onboarding-company-website-hint'}
							disabled={isSubmitting}
							className="h-14 text-base"
						/>
						{showWebsiteError
							? (
								<p id="onboarding-company-website-error" className="text-sm text-destructive">
									{t('onboarding.company.websiteInvalid')}
								</p>
							)
							: (
								<p id="onboarding-company-website-hint" className="text-sm text-muted-foreground">
									{t('onboarding.company.websiteHint')}
								</p>
							)}
					</>
				)}
				<Button
					type="submit"
					disabled={isSubmitting || trimmedName === ''}
					className="mt-3 h-14 bg-brand-600 hover:bg-brand-700 text-white"
				>
					{t('onboarding.company.submit')}
				</Button>
			</form>
		</>
	)
}
